import React, { useContext, useEffect, useState, useRef } from "react";
import axios from "axios";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AuthContext } from "../../../context/AuthContext";
import { WebexStatisticsColumn } from "../../../components/headerContent";
import ChartsAllStatistics from "./TypeCharts/ChartsAllStatistics";
import TableComponentStatistics from "./TableCompnentStatistics";
import SearchBarWebex from "./SearchBarWebex";
import StatisticsBarWebex from "./StatisticsBarWebex";
import StatisticsReportWebex from "./Rapport/StatisticsReportWebex";
import DepartmentExportButton from "./DepartmentExportButton";
import { Link } from "react-router-dom";
import CallQueue from "./CallQueue/CallQueue";
import useWindowSize from "../../../components/WindowSize";
import { DateRangePicker, Progress } from 'rsuite';
import { startOfDay, endOfDay, addDays, addMonths, subMonths, subDays, startOfMonth, endOfMonth } from 'date-fns';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { FileDownload, MoreVert } from "@mui/icons-material";
import Loader from "../../../components/Loader";
import { useTranslation } from "react-i18next";

const ranges = [
    { 
        label: 'Aujourd\'hui', 
        value: [startOfDay(new Date()), endOfDay(new Date())], 
        placement: 'left' 
    },
    { 
        label: 'Hier', 
        value: [startOfDay(addDays(new Date(), -1)), endOfDay(addDays(new Date(), -1))], 
        placement: 'left' 
    },
    { 
        label: 'Les 7 derniers jours', 
        value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())], 
        placement: 'left' 
    },
    { 
        label: 'Les 30 derniers jours', 
        value: [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())], 
        placement: 'left' 
    },
    { 
        label: 'Ce mois-ci', 
        value: [startOfMonth(new Date()), endOfDay(new Date())], 
        placement: 'left' 
    },
    { 
        label: 'Le mois dernier', 
        value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))], 
        placement: 'left' 
    }
];

const originalLabels = {
    "Location": "location",
    "Answered": "answerindicator",
    "Calltype": "calltype",
    "Direction": "direction"
};

export default function StatisticsWebex() {
    const { apiUrl, user } = useContext(AuthContext);
    const [dataWebexHistoryCall, setDataWebexHistoryCall] = useState(null);
    const [filters, setFilters] = useState([]);
    const [dataFiltered, setDataFiltered] = useState([]);
    const [allDataReport, setAllDataReport] = useState([]);
    const [firstDataReport, setFirstDataReport] = useState();
    const [firstDataTable, setFirstDataTable] = useState([]);
    const [linkPath, setLinkPath] = useState('statistics');
    const [loading, setLoading] = useState(false);
    const [messageRefresh, setMessageRefresh] = useState('');
    const [loadingPercent, setLoadingPercent] = useState(0);
    const [loadingColor, setLoadingColor] = useState('#3385ff');
    const today = endOfDay(new Date(), 23);
    const week = startOfDay(subDays(today, 7));
    const { afterToday, combine } = DateRangePicker;
    const [date, setDate] = useState([week, today]);
    const [minDate, setMinDate] = useState(null);
    const windowSize = useWindowSize();
    const placement = windowSize.width < 945 ? 'bottomStart' : 'bottomEnd';
    const [isRefresh, setIsRefresh] = useState(false);
    const chartsRef = useRef();
    const { t } = useTranslation();

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toISOString().split('T')[0];
    };

    const getDates = () => {
        const [startDate, adjustedEndDate] = date;
        const endDate = endOfDay(adjustedEndDate, 23);
        return { startDate, endDate };
    };

    const { startDate, endDate } = getDates();
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    useEffect(() => {
        setDataWebexHistoryCall(null);
    }, [date]);

    const getWebexHistory = async () => {
        try {
            const res = await axios.get(`${apiUrl}webex/calling/webexHistoryCall/${formattedStartDate}/${formattedEndDate}`, {
                withCredentials: true,
            });

            return res.data;
        } catch (error) {
        }
    };

    const webexHistoryCall = useQuery({
        queryKey: ["webexHistoryCall", formattedStartDate, formattedEndDate],
        queryFn: getWebexHistory
    });

    const getWebexPeople = async () => {
        try {
            const res = await axios.get(`${apiUrl}webex/people`, {
                withCredentials: true
            });
            return res.data;
        } catch (err) {
        }
    }

    const webexPeople = useQuery({
        queryKey: ["webexPeople"],
        queryFn: getWebexPeople,
    });

    const getWebexHistoryAll = async () => {
        try {
            const startDate = subMonths(today, 1);
            const startDateFormatted = formatDate(startDate);
            // const startDateFormatted = formatDate(webexHistoryCall.data[1]);
            const endDateFormatted = formatDate(today);
            const res = await axios.get(`${apiUrl}webex/calling/webexHistoryCall/${startDateFormatted}/${endDateFormatted}`, {
                withCredentials: true,
            });

            return res.data;
        } catch (error) {
            console.error("Error fetching Webex history all:", error);
        }
    };

    const webexHistoryCallAll = useQuery({
        queryKey: ["webexHistoryCallAll"],
        queryFn: getWebexHistoryAll,
        enabled: !!webexHistoryCall.data,
        keepPreviousData: true,
    });

    const getRefresh = async () => {
        try {
            const res = await axios.get(`${apiUrl}webex/calling/historyCall`, {
                withCredentials: true,
            });
            setMessageRefresh(t('Les appels ont été mis à jour avec succès !'));
            setIsRefresh('update');
            return res.data;
        } catch (error) {
            if (error.response?.data?.message?.startsWith("Too many requests sent for this OrgId")) {
                // Trop de requêtes envoyées, afficher le message correspondant
                setMessageRefresh(t('Veuillez patienter un moment avant de réessayer !'));
                setIsRefresh('reset');
            } else {
                setMessageRefresh(t('Erreur lors du rafraîchissement des appels !'));
                setIsRefresh('delete');
            }
        }
    };

    const { mutate: refresh, isPending: isRefreshing } = useMutation({
        mutationFn: getRefresh,
        onMutate: () => {
            setLoading(true); // Start loading
            setLoadingPercent(0); // Reset progress
            setLoadingColor('#124188'); // Initial loading color
            const interval = setInterval(() => {
                setLoadingPercent((prev) => {
                    const newPercent = Math.min(prev + 5, 100); // Increase loading percentage but cap at 90%
                    return newPercent;
                });
            }, 100); // Adjust interval as needed

            return { interval }; // Context shared to callbacks
        },
        onSuccess: () => {
            setLoading(false); // Stop loading
            setLoadingColor('#AEC353');
        },
        onError: () => {
            setLoading(false); // Stop loading
            setLoadingColor('#fff3f3'); // Red color for error
        },
        onSettled: (_data, _error, _variables, context) => {
            if (context?.interval) {
                clearInterval(context.interval);
            }
        }
    });

    useEffect(() => {
        if (webexHistoryCall.data && webexPeople.data) {
            if (webexHistoryCall.data[1]) {
                const earliestDate = new Date(webexHistoryCall.data[1]);
                setMinDate(earliestDate);
            }
            const filteredDatabyPeople = dataPeople(webexHistoryCall.data[0], webexPeople.data);
            const filteredDataFirstCall = filterDataByFirstCall(filteredDatabyPeople);
            setFirstDataTable(filterDataByPeriodFirstCallTable(filteredDataFirstCall));
            getUtilizationInterfaceGraph(filterDataByPeriodFirstCall(filteredDataFirstCall));
        }
    }, [webexHistoryCall.data, webexPeople.data, date]);

    useEffect(() => {
        if (webexHistoryCallAll.data && webexPeople.data) {
            if (webexHistoryCallAll.data[1]) {
                const earliestDate = new Date(webexHistoryCallAll.data[1]);
                setMinDate(earliestDate);
            }
            const filteredDatabyPeople = dataPeople(webexHistoryCallAll.data[0], webexPeople.data);
            setDataFiltered(filteredDatabyPeople);
            const filteredDataFirstCall = filterDataByFirstCall(filteredDatabyPeople);
            const filteredDataAllCalls = filterDataByAllCalls(filteredDatabyPeople);
            setFirstDataReport(filterFirstDataByPeriod(filteredDataFirstCall));
            setAllDataReport(filteredDataAllCalls);
            //getUtilizationInterfaceGraph(filterDataByPeriodFirstCall(filteredDataFirstCall));
        }
    }, [webexHistoryCallAll.data, webexPeople.data, date]);

    const getUtilizationInterfaceGraph = (dataWebexHistoryCall) => {
        let data = null;
        data = dataWebexHistoryCall.filter(item => { return item });
        setDataWebexHistoryCall(dataWebexHistoryCall);
    }

    const dataPeople = (data, people) => {
        if (!data || !people) {
            return [];
        }

        const isDisplayableValue = (value) => {
            const normalized = String(value || '').trim();
            return normalized
                && normalized !== 'NA'
                && normalized.toLowerCase() !== 'null'
                && normalized.toLowerCase() !== 'null null'
                && normalized.toLowerCase() !== 'undefined undefined';
        };

        const getPersonDisplayName = (person) => {
            const displayName = [person?.firstName, person?.lastName]
                .map(value => String(value || '').trim())
                .filter(isDisplayableValue)
                .join(' ');

            return isDisplayableValue(displayName) ? displayName : null;
        };

        const peopleMap = new Map();
        people.forEach(person => {
            const displayName = getPersonDisplayName(person);
            if (!displayName) {
                return;
            }

            if (person.phone && person.phone.work) {
                peopleMap.set(person.phone.work, displayName);
            } else if (person.phone && !person.phone.work && person.phone.mobile) {
                peopleMap.set(person.phone.mobile, displayName);
            }
        });

        const updateCallingLineId = (calls) => {
            if (!calls) {
                return;
            }

            calls.forEach(call => {
                const personInfoCalling = peopleMap.get(call.callingnumber);
                const personInfoCalled = peopleMap.get(call.callednumber);
                const personInfoCallerId = peopleMap.get(call.calleridnumber);

                if (personInfoCalling) {
                    call.callinglineid = personInfoCalling;
                } else if (user.holdingId === 33 && personInfoCallerId) {
                    call.callinglineid = personInfoCallerId;
                }
                if (user.holdingId === 33 && call.callednumber === "8888") {
                    call.calledlineid = t("Messagerie Vocale");
                }
                if (personInfoCalled) {
                    call.calledlineid = personInfoCalled;
                }
            });
        };

        const updatedData = Object.values(data).map(item => {
            if (!item) {
                return null;
            }

            if (!item.legs) {
                return { ...item, legs: {} };
            }

            Object.values(item.legs).forEach(leg => {
                if (!leg) {
                    return;
                }

                if (leg.calls) {
                    updateCallingLineId(leg.calls);
                }

                if (leg.children) {
                    Object.values(leg.children).forEach(child => {
                        if (child && child.calls) {
                            updateCallingLineId(child.calls);
                        }
                    });
                }
            });

            return item;
        }).filter(item => item !== null);

        return updatedData;
    };


    const filterDataByFirstCall = (data) => {
        if (!data) {
            return [];
        }

        return data.map(entry => {
            if (!entry || entry === "<") {
                return null;
            }

            if (!entry.legs) {
                return { ...entry, legs: {} };
            }

            const filteredLegs = Object.entries(entry.legs).reduce((acc, [key, leg]) => {
                if (leg.calls && leg.calls.length > 0) {
                    acc[key] = { ...leg };
                }
                return acc;
            }, {});

            return {
                ...entry,
                legs: filteredLegs
            };
        }).filter(entry => entry !== null && entry.legs && Object.keys(entry.legs).length > 0);
    };


    const filterDataByAllCalls = (data) => {
        if (!data) {
            return [];
        }

        const traverseAndExtractValidCalls = (node, correlationid) => {
            if (!node) {
                return null;
            }

            let calls = [];

            if (node.calls && node.calls.length > 0) {
                const callsWithCorrelationId = node.calls.map(call => ({
                    ...call,
                    correlationid
                }));
                calls = calls.concat(callsWithCorrelationId);
            }

            if (node.children) {
                for (const child of Object.values(node.children)) {
                    const childCalls = traverseAndExtractValidCalls(child, correlationid);
                    if (childCalls) {
                        calls = calls.concat(childCalls);
                    } else if (!node.calls || node.calls.length === 0) {
                        return null;
                    }
                }
            }

            return calls.length > 0 ? calls : null;
        };

        const filteredData = data.flatMap((entry) => {
            if (!entry || entry === "<") {
                return [];
            }

            const correlationid = entry.correlationid;

            if (!entry.legs) {
                return [];
            }

            const legCalls = Object.values(entry.legs).flatMap(leg => traverseAndExtractValidCalls(leg, correlationid) || []);
            return legCalls;
        }).filter(Boolean);

        return filteredData;
    };


    const filterFirstDataByPeriod = (data) => {
        return data.flatMap(entry => {
            const legEntries = Object.values(entry.legs);
            const firstLeg = legEntries[0]; // Sélectionner le premier `leg`
            if (!firstLeg) return []; // Si aucun `leg` n'est présent, retourner un tableau vide
            return [firstLeg.calls[0]]; // Retourner les appels filtrés
        });
    };

    const filterDataByPeriodFirstCallTable = (data) => {
        const { startDate, endDate } = getDates();
        return data.filter(entry => {
            const legEntries = Object.entries(entry.legs);
            return legEntries.some(([key, leg]) => {
                return leg.calls.some(call => {
                    const callStartTime = new Date(call.starttime);
                    return callStartTime >= startDate && callStartTime <= endDate;
                });
            });
        });
    };

    const filterDataByPeriodFirstCall = (data) => {
        const { startDate, endDate } = getDates();
        return data.flatMap(entry => {
            const legEntries = Object.values(entry.legs);
            const firstLeg = legEntries[0]; // Sélectionner le premier `leg`
            if (!firstLeg) return []; // Si aucun `leg` n'est présent, retourner un tableau vide
            // Filtrer les appels dans le premier `leg`
            const filteredCalls = [firstLeg.calls[0]].filter(call => {
                const callStartTime = new Date(call.starttime);
                return callStartTime >= startDate && callStartTime <= endDate;
            });
            return filteredCalls;
        });
    };

    const generatePdf = async () => {
        const element = chartsRef.current;

        const canvas = await html2canvas(element, {
            scale: 1.5,
            ignoreElements: (el) => el.classList.contains('table-legs-webex-statistics-content')
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.75);

        const pdf = new jsPDF();
        const imgProperties = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();

        // Définir une largeur stable pour toutes les images
        const stableWidth = 160; // Ajustez selon vos besoins
        const imgHeight = (imgProperties.height * stableWidth) / imgProperties.width; // Hauteur proportionnelle

        // Charger le logo
        const logoSrc = '/elitlogo.jpg'; // Remplacez par le chemin de votre logo
        try {
            const logo = await loadImage(logoSrc);

            // Ajouter le logo au PDF
            const logoWidth = 30; // Ajustez selon vos besoins
            const logoHeight = (logo.height * logoWidth) / logo.width; // Hauteur proportionnelle
            const logoX = 10; // Position X du logo
            const logoY = 10; // Position Y du logo

            pdf.addImage(logo, 'JPEG', logoX, logoY, logoWidth, logoHeight);

            // Définir les titres
            const title1 = t("ECP - Rapport Interactif"); // Premier titre
            const title2 = t("Statistics"); // Deuxième titre
            const titleMargin = 20; // Marge en haut pour le premier titre

            // Ajouter le premier titre
            pdf.setFontSize(18); // Taille de police pour le titre
            pdf.text(title1, pdfWidth / 2, titleMargin, { align: "center" }); // Titre centré

            // Ajouter le deuxième titre juste en dessous
            pdf.setFontSize(14); // Taille de police pour le sous-titre
            const title2Margin = titleMargin + 10; // Espace pour le deuxième titre
            pdf.text(title2, pdfWidth / 2, title2Margin, { align: "center" }); // Sous-titre centré

            // Centrer l'image horizontalement et ajuster position Y
            const positionX = (pdfWidth - stableWidth) / 2; // Centrer horizontalement
            const positionY = title2Margin + 10; // Position Y après le sous-titre

            // Ajouter l'image au PDF
            pdf.addImage(imgData, "JPEG", positionX, positionY, stableWidth, imgHeight);

            // Retourner le blob PDF
            pdf.save("interface-data.pdf");
        } catch (error) {
            console.error("Erreur lors du chargement de l'image du logo :", error);
        }
    };

    const loadImage = (src) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve(img);
            img.onerror = (err) => reject(err);
        });
    };

    const handleNavigation = (path) => {
        setLinkPath(path);
    };

    const handleGraphClick = (event, chartRef) => {
        const chartInstance = chartRef;
        const activePoints = chartInstance.getElementsAtEventForMode(
            event,
            "nearest",
            { intersect: true },
            true
        );
        if (activePoints.length > 0) {
            let column;
            const firstPoint = activePoints[0];
            const untranslatedLabel = chartInstance.data.datasets[firstPoint.datasetIndex].untranslatedLabel;
            let filter = chartInstance.data.labels[firstPoint.index];
            if (filter === 'Entrant' || filter === 'Incoming') {
                filter = 'TERMINATING';
            } else if (filter === 'Sortant' || filter === 'Outgoing') {
                filter = 'ORIGINATING';
            } else if (filter === 'Oui') {
                filter = 'Yes';
            } else if (filter === 'Non') {
                filter = 'No';
            } else if (filter === 'Redirection-d\'appel' || filter === "Call redirection") {
                filter = 'Yes-PostRedirection';
            } else if (filter === 'Interne') {
                filter = 'SIP_ENTERPRISE';
            } else if (filter === 'National') {
                filter = 'SIP_NATIONAL';
            } else if (filter === 'Mobile') {
                filter = 'SIP_MOBILE';
            } else if (filter === 'Inconnu') {
                filter = 'UNKNOWN';
            } else if (filter === 'Prenium') {
                filter = 'SIP_PREMIUM';
            } else if (filter === 'International') {
                filter = 'SIP_INTERNATIONAL';
            } else if (filter === 'Entrant') {
                filter = 'SIP_INBOUND';
            } else if (filter === 'Opérateur') {
                filter = 'SIP_OPERATOR';
            } else if (filter === 'Sans frais') {
                filter = 'SIP_TOLLFREE';
            } else if (filter === 'Réunion') {
                filter = 'SIP_MEETING';
            }
            column = originalLabels[untranslatedLabel] || untranslatedLabel;
            setFilters(prevFilters => {
                const existingFilter = prevFilters.find(f => f.column === column);
                if (existingFilter) {
                    const updatedValues = Array.from(new Set([...existingFilter.values, filter]));
                    return prevFilters.map(f => f.column === column ? { ...f, values: updatedValues } : f);
                } else {
                    return [...prevFilters, { column, values: [filter] }];
                }
            });
        }
    };

    const handleClickPopup = () => {
        setIsRefresh(false);
    }

    const hasNoWebexHistoryAtAll =
        webexHistoryCall.isFetched &&
        !webexHistoryCall.isLoading &&
        webexHistoryCall.data &&
        webexHistoryCall.data[0].length === 0 &&
        !webexHistoryCall.data[1];

    if (hasNoWebexHistoryAtAll) {
        return (
            <div className="ecp-container">
                <div className="ecp-container-content-error">
                    <div className="error-container">
                        <img src='../assets/ecp_error.svg' alt="icône d'erreur" />
                        <div className="error-container-text">
                            <h1 className="page-title">
                                {t("Aucune donnée disponible")}
                            </h1>
                            <p className="page-link_home">
                                <Link to="/"> {t("Revenir à l'accueil")}</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const isDataReady = !webexHistoryCall.isLoading && !!dataWebexHistoryCall;

    return (
        <div className="ecp-container">
            <div className="ecp-container-content">
                {isRefresh && (
                    <article className="page-timeline">
                        <div className={`timeline-webhook-container popup-${isRefresh}`}>
                            <button onClick={() => handleClickPopup()} className="close-alerts-status-length">
                                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="xmark" className="svg-inline--fa fa-xmark " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                                    <path fill="currentColor" d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z"></path>
                                </svg>
                            </button>
                            <div>{messageRefresh}</div>
                        </div>
                    </article>
                )}
                <>
                    <div className="statistics-header-content">
                        <section className="page-logo">
                            <img src="./assets/webex_logo.png" alt="Logo de Cisco Webex" />
                            <h1 className="page-title">Statistics Webex Calling</h1>
                        </section>
                        {linkPath === 'statistics' && (
                            <section className="choice-calendar-or-pdf">
                                <DateRangePicker
                                    ranges={ranges}
                                    cleanable={false}
                                    character=" / "
                                    value={date}
                                    onChange={setDate}
                                    format="dd-MM-yyyy"
                                    defaultValue={[week, today]}
                                    shouldDisableDate={combine(afterToday(), (date) => minDate && date < startOfDay(new Date(minDate)))}
                                    locale={{
                                        sunday: t('Su'),
                                        monday: t('Mo'),
                                        tuesday: t('Tu'),
                                        wednesday: t('We'),
                                        thursday: t('Th'),
                                        friday: t('Fr'),
                                        saturday: t('Sa'),
                                        hours: t('Hours'),
                                    }}
                                    placement={placement}
                                />
                                <DepartmentExportButton user={user} apiUrl={apiUrl} dates={date} />
                                <div className='tooltip-csv-tableWebex'>
                                    <MoreVert />
                                    <span className='tooltip-csv-tableWebex-button'>
                                        <div onClick={generatePdf} className="saveCSVWebexCalling"><FileDownload /> {t("Save as PDF")}</div>
                                        <div
                                            onClick={() => { refresh(); }}
                                            className="saveCSVWebexCalling"
                                        >
                                            <div className="content-refresh-button-excel">
                                                <div className="name-refresh">
                                                    <FileDownload /> {t("Refresh calls")}
                                                </div>
                                                {loading && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <Progress.Line percent={loadingPercent} strokeColor={loadingColor} status={loadingPercent >= 50 ? 'active' : null} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </span>
                                </div>
                            </section>
                        )}
                    </div>
                    <div className="menu-statistics-webex">
                        <div onClick={() => handleNavigation('statistics')} className={linkPath === 'statistics' ? 'active' : ''}>{t("Detailed Call History")}</div>
                        <div onClick={() => handleNavigation('rapport')} className={linkPath === 'rapport' ? 'active' : ''}>{t("Rapport interactif")}</div>
                        <div onClick={() => handleNavigation('call_queue')} className={linkPath === 'call_queue' ? 'active' : ''}>{t("Call Queue")}</div>
                    </div>
                    {linkPath === 'statistics' && (
                        <div>
                            <SearchBarWebex dataWebex={dataWebexHistoryCall} dataWebexColumns={WebexStatisticsColumn} filters={filters} setFilters={setFilters} />
                        </div>
                    )}
                    {linkPath === 'statistics' ? (
                        <div>
                            <div className="container-bar-statistics">
                                <StatisticsBarWebex data={dataWebexHistoryCall} dataAll={firstDataReport} filters={filters} date={date} statusLoadingData={isDataReady} />
                            </div>
                            <div className="container-charts" ref={chartsRef}>
                                <ChartsAllStatistics data={dataWebexHistoryCall} filters={filters} date={date} handleGraphClick={handleGraphClick} statusLoadingData={isDataReady} />
                            </div>
                            <div className="table-legs-webex-statistics-content">
                                <TableComponentStatistics tableData={firstDataTable} tableColumns={WebexStatisticsColumn} statusLoadingData={isDataReady} user={user} />
                            </div>
                        </div>
                    ) : linkPath === 'rapport' ? (
                        !webexHistoryCall.isLoading && webexHistoryCall.data && dataWebexHistoryCall ? (
                            <StatisticsReportWebex data={firstDataReport} people={webexPeople.data} />
                        ) : (
                            <Loader />
                        )
                    ) : (
                        <>
                            {!webexHistoryCallAll.isLoading && !webexPeople.isLoading ? (
                                <CallQueue data={allDataReport} allData={dataFiltered} people={webexPeople.data} />
                            ) : (
                                <Loader />
                            )}
                        </>
                    )}
                </>
            </div>
        </div>
    );
}
